import { formatDay, oneSpec } from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    DateTime,
    Field,
    Input,
    PageHeader,
    Section,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useSearchParams } from 'react-router-dom';

import type { RiskDashboard } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { useOne } from '@/lib/queries';
import {
    AttemptsChart,
    DeclinesChart,
    EnumerationChart,
    QueueChart,
    RatioTrendChart,
    RuleHeatmap,
    ScoreChart,
} from '@/pages/risk/dashboard-charts';
import { CurveChart } from '@/pages/risk/learning';
import { metricLabel, versionLabel } from '@/pages/risk/models';

const PATH = '/staff/risk/dashboard';
const RANGES = [
    ['last_7_days', 'Last 7 days'],
    ['last_30_days', 'Last 30 days'],
    ['this_month', 'This month'],
    ['last_month', 'Last month'],
    ['this_quarter', 'This quarter'],
] as const;

/** The dashboard's query from the URL: the range preset and an optional merchant or store. */
export function dashboardParams(
    search: URLSearchParams,
): Record<string, string> {
    const out: Record<string, string> = {
        range: search.get('range') ?? 'last_30_days',
    };
    for (const key of ['merchant_id', 'store_id']) {
        const v = search.get(key)?.trim();
        if (v) out[key] = v;
    }
    return out;
}

export const riskDashboardReads = (url: URL) => ({
    dashboard: oneSpec(PATH, dashboardParams(url.searchParams)),
});

/** BM-125 decision 17: Risk → Dashboard, the engine's activity and the merchants' ratios on one page. */
export function RiskDashboardPage() {
    const [search, setSearch] = useSearchParams();
    const params = dashboardParams(search);
    const query = useOne<RiskDashboard>(PATH, params);
    const set = (key: string, value: string) => {
        const next = new URLSearchParams(search);
        if (value.trim() === '') next.delete(key);
        else next.set(key, value.trim());
        setSearch(next, { replace: true });
    };
    return (
        <div>
            <PageHeader
                title="Risk dashboard"
                meta={<AsOfBadge asOf={query.freshness.asOf} />}
            />
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field id="rd-range" label="Window">
                    <Select
                        value={params.range}
                        onValueChange={(v) => set('range', v)}
                    >
                        <SelectTrigger id="rd-range">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {RANGES.map(([v, label]) => (
                                <SelectItem key={v} value={v}>
                                    {label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field
                    id="rd-merchant"
                    label="Merchant"
                    hint="Empty: the whole tenant"
                >
                    <Input
                        id="rd-merchant"
                        placeholder="mer_…"
                        defaultValue={params.merchant_id ?? ''}
                        onBlur={(e) => set('merchant_id', e.target.value)}
                    />
                </Field>
                <Field id="rd-store" label="Store" hint="Empty: every store">
                    <Input
                        id="rd-store"
                        placeholder="sto_…"
                        defaultValue={params.store_id ?? ''}
                        onBlur={(e) => set('store_id', e.target.value)}
                    />
                </Field>
            </div>
            <DetailBody query={query} title="Risk dashboard">
                {(d) => (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Section
                            title="Attempts"
                            description={`${formatDay(d.from)} to ${formatDay(d.to)}, by the check's verdict`}
                        >
                            <AttemptsChart rows={d.attempts} />
                        </Section>
                        {/* slice 5 (decisions 13, 45): the model the checks score with and today's appetite curve */}
                        <Section
                            title="Model"
                            description="What the checks score with (decision 45)"
                        >
                            {d.model ? (
                                <p
                                    className="m-0 text-sm"
                                    data-testid="dashboard-model"
                                >
                                    {d.model.scope} model of{' '}
                                    {versionLabel(d.model.version)}, trained{' '}
                                    <DateTime
                                        value={d.model.trained_at}
                                        format="date"
                                    />
                                    , AP {metricLabel(d.model.metrics.ap)},
                                    precision at {d.model.metrics.k ?? '—'} a
                                    day {metricLabel(d.model.metrics.cp_at_k)}.
                                </p>
                            ) : (
                                <p className="text-fg-2 m-0 text-sm">
                                    No active model: the ML rules read
                                    unavailable until staff activate a candidate
                                    on Models.
                                </p>
                            )}
                        </Section>
                        <Section title="Declines by reason">
                            <DeclinesChart rows={d.declines_by_reason} />
                        </Section>
                        <Section
                            title="Review queue"
                            description={`${d.queue.open} open, ${d.queue.late} late`}
                        >
                            <QueueChart queue={d.queue} />
                        </Section>
                        <Section
                            title="Scores"
                            description="Pre-authorization checks by score, with the bands"
                        >
                            <ScoreChart
                                rows={d.score_histogram}
                                bands={d.bands}
                            />
                        </Section>
                        <div className="md:col-span-2">
                            <Section
                                title="Ratio trends"
                                description="Monthly ratios against the card networks' lines"
                            >
                                <RatioTrendChart
                                    rows={d.ratio_trends}
                                    warningBps={d.network_lines.warning_bps}
                                    breachBps={d.network_lines.breach_bps}
                                />
                            </Section>
                        </div>
                        <div className="md:col-span-2">
                            <Section
                                title="Appetite curve"
                                description="Share declined against fraud caught, per preset (computed on Profiles)"
                            >
                                {d.curve ? (
                                    <CurveChart curve={d.curve} />
                                ) : (
                                    <p className="text-fg-2 m-0 text-sm">
                                        Not computed today: open Profiles and
                                        show the curve.
                                    </p>
                                )}
                            </Section>
                        </div>
                        <div className="md:col-span-2">
                            <Section
                                title="Rule hits"
                                description="The 20 busiest rules per day"
                            >
                                <RuleHeatmap rows={d.rule_hits} />
                            </Section>
                        </div>
                        <Section
                            title="Enumeration"
                            description="The most declines on one BIN in one hour"
                        >
                            <EnumerationChart rows={d.enumeration} />
                        </Section>
                        <Section title="Cases">
                            {d.cases_by_kind.length === 0 ? (
                                <p className="text-fg-2 m-0 text-sm">
                                    No cases opened in this window.
                                </p>
                            ) : (
                                <Table data-testid="dashboard-cases">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Kind</TableHead>
                                            <TableHead className="text-right">
                                                Open
                                            </TableHead>
                                            <TableHead className="text-right">
                                                Opened
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {d.cases_by_kind.map((c) => (
                                            <TableRow key={c.kind}>
                                                <TableCell>
                                                    <Badge>{c.kind}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {c.open}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {c.total}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Section>
                        <div className="md:col-span-2">
                            <Section
                                title="Feeds"
                                description="A feed is stale 24 hours after its last success"
                            >
                                <div className="overflow-x-auto">
                                    <Table data-testid="dashboard-feeds">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Feed</TableHead>
                                                <TableHead>
                                                    Last success
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    Rows
                                                </TableHead>
                                                <TableHead>Health</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {d.feeds.map((f) => (
                                                <TableRow key={f.name}>
                                                    <TableCell className="font-mono text-xs">
                                                        {f.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <DateTime
                                                            value={
                                                                f.last_success_at
                                                            }
                                                            empty="Never"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">
                                                        {f.rows ?? '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusBadge
                                                            status={
                                                                f.stale
                                                                    ? 'stale'
                                                                    : f.last_success_at
                                                                      ? 'fresh'
                                                                      : 'never'
                                                            }
                                                            domain={{
                                                                stale: [
                                                                    'danger',
                                                                    'Stale',
                                                                ],
                                                                fresh: [
                                                                    'success',
                                                                    'Fresh',
                                                                ],
                                                                never: [
                                                                    'neutral',
                                                                    'Not loaded',
                                                                ],
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </Section>
                        </div>
                    </div>
                )}
            </DetailBody>
        </div>
    );
}
